/**
 * Pages Google should offer for Bethlehem Homeschool Group.
 * The public site is the landing page. Google may index that page, its
 * sections, Schedule a Visit, Log in, and Create an account. Pages behind log in, password
 * reset, and legal pages are noindex so they are not showcased.
 *
 * The same path list is copied into the inline script in index.html and
 * into public/sitemap.xml. publicSearch.test.ts fails if those drift.
 */

export const SITE_ORIGIN = 'https://bethlehemhomeschool.group';
export const SITE_NAME = 'Bethlehem Homeschool Group';

export const HOME_TITLE = 'BHG! - Bethlehem Homeschool Group - Knightdale, NC';

export const HOME_DESCRIPTION =
  'Bethlehem Homeschool Group serves homeschool families in Knightdale, Clayton, Raleigh, Wake Forest, Wendell, Zebulon, and the greater Triangle, NC. Christian-led education and support in Knightdale, North Carolina.';

const LOGIN_DESCRIPTION =
  'Log in to Bethlehem Homeschool Group to manage your family, classes, and registration.';

const REGISTER_DESCRIPTION =
  'Create an account with Bethlehem Homeschool Group, the Christian homeschool group in Knightdale, NC.';

const VISIT_DESCRIPTION =
  'Schedule a visit to Bethlehem Homeschool Group at Bethlehem Baptist Church in Knightdale, NC.';

function pageTitle(label: string): string {
  return `${label} | ${SITE_NAME}`;
}

export interface LandingSectionPage {
  path: string;
  sectionId: string;
  /** Nav label. Also the start of the page title Google uses for a sitelink. */
  label: string;
  title: string;
  description: string;
}

export const LANDING_SECTIONS: readonly LandingSectionPage[] = [
  {
    path: '/about',
    sectionId: 'about',
    label: 'About',
    title: pageTitle('About'),
    description:
      'What Bethlehem Homeschool Group is: a Christian homeschool group meeting Thursdays in Knightdale, NC.',
  },
  {
    path: '/mission',
    sectionId: 'mission',
    label: 'Our Mission and Vision',
    title: pageTitle('Our Mission and Vision'),
    description:
      'The mission and vision of Bethlehem Homeschool Group, a Christian homeschool community in Knightdale, NC.',
  },
  {
    path: '/our-story',
    sectionId: 'our-story',
    label: 'Our Story',
    title: pageTitle('Our Story'),
    description:
      'How Bethlehem Homeschool Group started in 2017 at Bethlehem Baptist Church in Knightdale, NC.',
  },
  {
    path: '/our-board',
    sectionId: 'our-board',
    label: 'Meet the Board',
    title: pageTitle('Meet the Board'),
    description:
      'Meet the board that leads Bethlehem Homeschool Group at Bethlehem Baptist Church in Knightdale, NC.',
  },
  {
    path: '/courses',
    sectionId: 'courses',
    label: 'Courses',
    title: pageTitle('Courses'),
    description:
      'Academic and enrichment courses for preschool through high school at Bethlehem Homeschool Group in Knightdale, NC.',
  },
  {
    path: '/join',
    sectionId: 'how-to-join',
    label: 'How to Join',
    title: pageTitle('How to Join'),
    description:
      'How families join Bethlehem Homeschool Group in Knightdale: review courses, register, and sign up for classes.',
  },
  {
    path: '/resources',
    sectionId: 'resources',
    label: 'Resources',
    title: pageTitle('Resources'),
    description:
      'Policies, guidelines, and documents for families at Bethlehem Homeschool Group in Knightdale, NC.',
  },
  {
    path: '/contact',
    sectionId: 'contact',
    label: 'Contact',
    title: pageTitle('Contact'),
    description:
      'Contact Bethlehem Homeschool Group at Bethlehem Baptist Church, 8400 Poole Road, Knightdale, NC 27545.',
  },
];

/** URL slugs for the landing layout route (`about`, not `/about`). */
export const LANDING_SECTION_SLUGS: readonly string[] = LANDING_SECTIONS.map((section) =>
  section.path.slice(1)
);

export const LOGIN_PATH = '/login';
export const REGISTER_PATH = '/register';
/** Opens the Schedule a Visit popup on the landing page. */
export const VISIT_PATH = '/visit';

/** Paths allowed in Google. Order matches the inline allowlist in index.html. */
export const INDEXABLE_PUBLIC_PATHS: readonly string[] = [
  '/',
  ...LANDING_SECTIONS.map((section) => section.path),
  LOGIN_PATH,
  REGISTER_PATH,
  VISIT_PATH,
];

export interface PublicNavSubLink {
  href: string;
  label: string;
  sectionId: string;
}

export interface PublicNavLink {
  href: string;
  label: string;
  sectionId: string;
  ariaLabel?: string;
  subLinks?: readonly PublicNavSubLink[];
}

export const PUBLIC_NAV_LINKS: readonly PublicNavLink[] = [
  {
    href: '/about',
    label: 'About',
    sectionId: 'about',
    subLinks: [
      { href: '/about', label: 'What is BHG?', sectionId: 'about' },
      { href: '/mission', label: 'Our Mission and Vision', sectionId: 'mission' },
      { href: '/our-story', label: 'Our Story', sectionId: 'our-story' },
      { href: '/our-board', label: 'Meet the Board', sectionId: 'our-board' },
    ],
  },
  { href: '/courses', label: 'Courses', sectionId: 'courses' },
  { href: '/join', label: 'Join', sectionId: 'how-to-join', ariaLabel: 'How to Join' },
  { href: '/resources', label: 'Resources', sectionId: 'resources' },
  { href: '/contact', label: 'Contact', sectionId: 'contact' },
];

export const ABOUT_SECTION_IDS: readonly string[] = ['about', 'mission', 'our-story', 'our-board'];

export const PUBLIC_FOOTER_RESOURCE_LINKS: readonly { href: string; label: string }[] = [
  { href: '/courses', label: 'Course Schedule' },
  { href: '/resources', label: 'Policies & Guidelines' },
  { href: '/resources', label: 'Disciplinary Action Plan' },
];

export interface PublicSearchMeta {
  title: string;
  description: string;
  canonicalUrl: string;
  robots: 'index, follow' | 'noindex, follow';
}

/** Drop a trailing slash and any hash or query so `/contact/` matches `/contact`. */
export function normalizePublicPath(pathname: string): string {
  const pathOnly = pathname.split('?')[0]?.split('#')[0] ?? '/';
  if (pathOnly === '' || pathOnly === '/') return '/';
  return pathOnly.replace(/\/+$/, '') || '/';
}

export function isIndexablePublicPath(pathname: string): boolean {
  return INDEXABLE_PUBLIC_PATHS.includes(normalizePublicPath(pathname));
}

/** Home, landing sections, and the visit popup. Log in and Create an account are separate pages. */
export function isLandingDocumentPath(pathname: string): boolean {
  const path = normalizePublicPath(pathname);
  if (path === '/' || path === VISIT_PATH) return true;
  return LANDING_SECTIONS.some((section) => section.path === path);
}

export function sectionIdForLandingPath(pathname: string): string | null {
  const path = normalizePublicPath(pathname);
  return LANDING_SECTIONS.find((section) => section.path === path)?.sectionId ?? null;
}

export function resolvePublicSearchMeta(pathname: string): PublicSearchMeta {
  const path = normalizePublicPath(pathname);
  if (path === '/') {
    return {
      title: HOME_TITLE,
      description: HOME_DESCRIPTION,
      canonicalUrl: `${SITE_ORIGIN}/`,
      robots: 'index, follow',
    };
  }
  const section = LANDING_SECTIONS.find((item) => item.path === path);
  if (section) {
    return {
      title: section.title,
      description: section.description,
      canonicalUrl: `${SITE_ORIGIN}${section.path}`,
      robots: 'index, follow',
    };
  }
  if (path === LOGIN_PATH) {
    return {
      title: pageTitle('Log in'),
      description: LOGIN_DESCRIPTION,
      canonicalUrl: `${SITE_ORIGIN}/login`,
      robots: 'index, follow',
    };
  }
  if (path === REGISTER_PATH) {
    return {
      title: pageTitle('Create an account'),
      description: REGISTER_DESCRIPTION,
      canonicalUrl: `${SITE_ORIGIN}/register`,
      robots: 'index, follow',
    };
  }
  if (path === VISIT_PATH) {
    return {
      title: pageTitle('Schedule a Visit'),
      description: VISIT_DESCRIPTION,
      canonicalUrl: `${SITE_ORIGIN}/visit`,
      robots: 'index, follow',
    };
  }
  return {
    title: HOME_TITLE,
    description: HOME_DESCRIPTION,
    canonicalUrl: `${SITE_ORIGIN}${path}`,
    robots: 'noindex, follow',
  };
}
